import React from 'react';

import { Plus, Exit, Group } from '@profitfy/pakkun-icons';
import { useHistory } from 'react-router-dom';
import { EAvatarSize } from '@domain/enums/components/EAvatar';
import { ETextWeight } from '@domain/enums/components/EText';
import {
  ESynchronizationRequestType,
  ESynchronizationType,
} from '@domain/enums/hooks/ESynchronization';
import { IStore } from '@domain/interfaces/stores/IStore';
import { useConfig } from '@helpers/hooks/useConfig';
import { useSynchronization } from '@helpers/hooks/common/useSynchronization';
import { LONG_API_DOMAIN } from '@constants/api';
import { getEnvironment } from '@helpers/utils/environment';
import { INavBarDropdownProps } from '@domain/interfaces/onboarding/components/navBar/INavBar';

import storesService from '@services/pages/stores/stores';

import * as S from './styles';

const Dropdown: React.FC<INavBarDropdownProps> = ({ children, storeAliasId }) => {
  const history = useHistory();

  const { handleLogout, isInternalTeam } = useConfig();
  const { addSynchronization } = useSynchronization();

  const { stores } = storesService.getStores({ page: 0, filter: {} });

  const handleAdmin = React.useCallback(async () => {
    history.push(`/${storeAliasId}/dashboard/admin-panel`);
  }, [history, storeAliasId]);

  const handleAddStore = React.useCallback(() => {
    history.push(`/stores`);
  }, [history]);

  const handleChangeStore = React.useCallback(
    newStoreAliasId => {
      history.push(`/${newStoreAliasId}/dashboard/overview/financial`);
    },
    [history],
  );

  const syncDemoData = React.useCallback(() => {
    addSynchronization({
      name: 'Demo',
      type: ESynchronizationType.DEMO_STORE,
      label: 'Demo',
      dependencies: [],
      config: {
        isOnboardingSynchronization: false,
        showNotification: true,
      },
      request: {
        type: ESynchronizationRequestType.POST,
        domain: LONG_API_DOMAIN,
        api: `/${getEnvironment()}/api/v1/users/stores/${storeAliasId}/demo/update-store-data`,
      },
    });
  }, [addSynchronization, storeAliasId]);

  const isDevEnvironment = getEnvironment() === 'dev';

  return (
    <S.CustomDropdown>
      <S.CustomDropdown.Trigger>{children}</S.CustomDropdown.Trigger>

      <S.CustomDropdown.Content sideOffset={12} align="end" style={{ borderRadius: 15 }}>
        <S.StoresWrapper>
          {stores?.map((store: IStore) => (
            <S.CustomDropdown.Item
              onClick={() => handleChangeStore(store.alias_id)}
              key={store.alias_id}
            >
              <S.StoreWrapper>
                <S.StoreAvatar
                  isCurrentStore={storeAliasId === store.alias_id}
                  word={store?.name.charAt(0) || 'N'}
                  size={EAvatarSize.SMALL}
                  alt="Store avatar"
                />
                <S.StoreName
                  isCurrentStore={storeAliasId === store.alias_id}
                  weight={ETextWeight.MEDIUM}
                >
                  {store?.name}
                </S.StoreName>
              </S.StoreWrapper>
            </S.CustomDropdown.Item>
          ))}
        </S.StoresWrapper>

        <S.CustomDropdown.Separator />

        <S.CustomDropdown.Item onClick={handleAddStore}>
          <S.NewStoreWrapper>
            <Plus size={16} color="gray" />
            <S.OptionName weight={ETextWeight.MEDIUM}>Adicionar nova loja</S.OptionName>
          </S.NewStoreWrapper>
        </S.CustomDropdown.Item>

        <S.CustomDropdown.Separator />

        <S.OptionsWrapper>
          {isInternalTeam && (
            <S.CustomDropdown.Item onClick={handleAdmin}>
              <S.OptionWrapper>
                <Group size={24} color="#D9DDE9" />
                <S.OptionName weight={ETextWeight.MEDIUM}>Painel de Admin</S.OptionName>
              </S.OptionWrapper>
            </S.CustomDropdown.Item>
          )}

          <S.CustomDropdown.Item onClick={handleLogout}>
            <S.OptionWrapper>
              <Exit size={20} color="#D9DDE9" />
              <S.OptionName weight={ETextWeight.MEDIUM}>Sair</S.OptionName>
            </S.OptionWrapper>
          </S.CustomDropdown.Item>

          {isDevEnvironment && (
            <S.CustomDropdown.Item onClick={syncDemoData}>
              <S.OptionWrapper>
                <S.OptionName weight={ETextWeight.MEDIUM}>Atualizar dados</S.OptionName>
              </S.OptionWrapper>
            </S.CustomDropdown.Item>
          )}
        </S.OptionsWrapper>

        <S.CustomDropdown.Arrow />
      </S.CustomDropdown.Content>
    </S.CustomDropdown>
  );
};

export default Dropdown;
